/**
 * Mirrors server {@link coerceIamPolicyDocumentForRuntime} / {@link IamPolicyExtract.listAllowActionsFromPolicy}
 * so admin console reads DB-backed policies whether they are camelCase (canonical) or PascalCase (legacy/AWS JSON).
 */
export type IamEffect = 'Allow' | 'Deny';

export interface IamStatement {
  Sid?: string;
  Effect: IamEffect;
  Action: string | string[];
  Resource: string | string[];
}

export interface IamPolicyDocument {
  Version: '2012-10-17';
  Statement: IamStatement[];
}

type CoercedStatement = { effect: string; action: unknown };

type CoercedPolicy = { version: string; statement: CoercedStatement[] };

function coerceIamPolicyDocumentForClient(doc: unknown): CoercedPolicy | null {
  if (doc == null || typeof doc !== 'object') {
    return null;
  }
  const d = doc as Record<string, unknown>;
  const stmtsUnion = (d.statement ?? d.Statement) as unknown;
  if (stmtsUnion == null) {
    return null;
  }
  const stmtsRaw = Array.isArray(stmtsUnion)
    ? stmtsUnion
    : typeof stmtsUnion === 'object'
      ? [stmtsUnion]
      : [];
  if (stmtsRaw.length === 0) {
    return null;
  }
  let version = (d.version ?? d.Version) as string | undefined;
  if (version !== '2012-10-17') {
    if (version == null || String(version).trim() === '') {
      version = '2012-10-17';
    } else {
      return null;
    }
  }

  const statement: CoercedStatement[] = stmtsRaw.map((st: unknown) => {
    const row = st as Record<string, unknown>;
    const isDeny = String(row.effect ?? row.Effect ?? '').toLowerCase() === 'deny';
    const effect: 'Allow' | 'Deny' = isDeny ? 'Deny' : 'Allow';
    const action = row.action ?? row.Action;
    return { effect, action };
  });

  return { version: '2012-10-17', statement };
}

function normalizeActionList(raw: unknown): string[] {
  if (raw == null || raw === '') {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw
      .flatMap((item): string[] => {
        if (item == null || item === '') {
          return [];
        }
        if (typeof item === 'string') {
          return [item.trim()].filter((value) => value.length > 0);
        }
        if (typeof item === 'object' && !Array.isArray(item)) {
          const o = item as Record<string, unknown>;
          const v = o.action ?? o.permission ?? o.Permission ?? o.Action;
          if (typeof v === 'string') {
            return [v.trim()].filter((s) => s.length > 0);
          }
          if (Array.isArray(v)) {
            return normalizeActionList(v as string[]);
          }
        }
        if (Array.isArray(item)) {
          return normalizeActionList(item as string[]);
        }
        return [String(item).trim()].filter((value) => value.length > 0);
      });
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const v = o.action ?? o.permission ?? o.Permission ?? o.Action;
    if (typeof v === 'string') {
      return [v.trim()].filter((s) => s.length > 0);
    }
    if (Array.isArray(v)) {
      return normalizeActionList(v as string[]);
    }
  }
  return [String(raw).trim()].filter((value) => value.length > 0);
}

export function listAllowActionsFromPolicy(doc: unknown): string[] {
  const canonical = coerceIamPolicyDocumentForClient(doc);
  if (!canonical?.statement?.length) {
    return [];
  }
  const actions = new Set<string>();
  for (const st of canonical.statement) {
    if (String(st.effect).toLowerCase() !== 'allow') {
      continue;
    }
    for (const a of normalizeActionList(st.action)) {
      actions.add(a);
    }
  }
  return [...actions].sort();
}

/**
 * Accepts multiple API shapes and extracts IAM allow actions:
 * - string[] action list
 * - `{ permissions: string[] }`
 * - `{ actions: string[] }`
 * - `{ iamPolicyDocument }`
 * - direct policy doc (camelCase or PascalCase)
 * - direct `IamPolicyDocument` (legacy client type with `Statement`)
 */
export function extractAllowActions(input: unknown): string[] {
  if (input == null) {
    return [];
  }

  if (Array.isArray(input)) {
    return input
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0)
      .sort();
  }

  if (typeof input !== 'object') {
    return [];
  }

  const record = input as Record<string, unknown>;

  if (record.iamPolicyDocument && typeof record.iamPolicyDocument === 'object') {
    const fromIam = listAllowActionsFromPolicy(record.iamPolicyDocument);
    if (fromIam.length > 0) {
      return fromIam;
    }
  }

  /** Legacy field — server migrates to {@link iamPolicyDocument} but older rows may still only have this. */
  if (record.policyDocument && typeof record.policyDocument === 'object') {
    const fromLegacy = listAllowActionsFromPolicy(record.policyDocument);
    if (fromLegacy.length > 0) {
      return fromLegacy;
    }
  }

  if (Array.isArray(record.permissions)) {
    return extractAllowActions(record.permissions);
  }

  if (Array.isArray(record.actions)) {
    return extractAllowActions(record.actions);
  }

  return listAllowActionsFromPolicy(input);
}

/**
 * Builds the policy document stored on {@code Role.iamPolicyDocument} for admin-console roles
 * (global {@code Resource: "*"}), matching {@link LegacyPermissionPolicyBuilder} defaults.
 */
export function buildAdminIamPolicyFromAllowActions(actions: string[]): IamPolicyDocument {
  const unique = [...new Set((actions || []).filter((p) => typeof p === 'string' && p.trim().length > 0))].sort();
  if (unique.length === 0) {
    throw new Error('Select at least one permission.');
  }
  return {
    Version: '2012-10-17',
    Statement: [
      {
        Sid: 'AdminConsoleAllow',
        Effect: 'Allow',
        Action: unique,
        Resource: '*',
      },
    ],
  };
}
